import React, { useState, useEffect, useMemo } from 'react';
import { Types } from 'mongoose';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { voiceItemTTSAudioASRSettings } from '../../../../util/getVoiceItemSettings';

import InputVoicebotAudioText from './InputVoicebotAudioText';
import SelectGrammar from './SelectGrammar';
import SelectStatus from './SelectStatus';
import ButtonTestTts from './ButtonTestTts';

export default ({
  doUpdateItem,
  item,
  bot,
  connectors,
  deleteConnectorRequest,
}) => {
  const [itemSettings, setItemSettings] = useState(
    voiceItemTTSAudioASRSettings
  );

  const { grammars } = useSelector((state) => state.voiceBot);

  useEffect(() => {
    if (item && item.voiceSettings) {
      setItemSettings(item.voiceSettings);
    }
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    const newValue = type === 'checkbox' ? checked : value;
    const updatedSettings = { ...itemSettings, [name]: newValue };

    setItemSettings(updatedSettings);
    doUpdateItem({ voiceSettings: updatedSettings });
  };

  const connectorsFromItem = useMemo(() => {
    return connectors?.filter(
      (connector) => String(connector.from) === String(item._id)
    );
  }, [connectors, item?._id]);

  const deleteConnectorsFromItem = () => {
    const { menuItems } = item;
    if (!menuItems) return;

    connectorsFromItem.forEach((connectorItem) => {
      deleteConnectorRequest(connectorItem._id, bot._id);
    });
  };

  const handleChangeMustValidateIvrResultInDatabase = (e) => {
    const { mustNextStepWithIvrData } = itemSettings;

    const isCheckedMustNextStepWithIvrData = mustNextStepWithIvrData;
    if (isCheckedMustNextStepWithIvrData) return;

    const hasConnectorFromItem = connectorsFromItem?.length > 0;

    const abortChange =
      hasConnectorFromItem &&
      !window.confirm(
        'Deseja realmente alterar este campo? (Essa ação vai deletar todas as ligações com origem neste bloco)'
      );

    if (abortChange) return;

    deleteConnectorsFromItem();

    let menuItems = [];

    const { name, checked } = e.target;

    if (checked) {
      menuItems = [
        {
          _id: Types.ObjectId().toString(),
          description: 'VALID',
          require_connection: true,
        },
        {
          _id: Types.ObjectId().toString(),
          description: 'LIMIT_EXCEEDED',
          require_connection: true,
        },
        {
          _id: Types.ObjectId().toString(),
          description: 'INVALID',
        },
      ];
    } else {
      menuItems = [
        {
          _id: Types.ObjectId().toString(),
          description: '',
        },
      ];
    }

    const updatedVoiceSettings = { ...itemSettings, [name]: checked };
    const updatedItem = {
      menuItems,
      voiceSettings: updatedVoiceSettings,
    };

    setItemSettings(updatedVoiceSettings);
    doUpdateItem(updatedItem);
  };

  const deleteGrammar = () => {
    const updatedVoiceSettings = {
      ...itemSettings,
      idIvrGrammar: null,
    };
    const updatedItem = {
      menuItems: [],
      voiceSettings: updatedVoiceSettings,
    };

    setItemSettings(updatedVoiceSettings);
    doUpdateItem(updatedItem);
  };

  const handleChangeGrammar = (e) => {
    const hasConnectorFromItem = connectorsFromItem?.length > 0;

    const abortChange =
      hasConnectorFromItem &&
      !window.confirm(
        'Deseja realmente alterar a gramática? (Essa ação vai deletar todas as ligações com origem neste bloco)'
      );

    if (abortChange) return;

    deleteConnectorsFromItem();

    const newGrammarId = e.target.value;

    if (!newGrammarId) {
      deleteGrammar();
      return;
    }

    const findGrammar = grammars?.find(
      (grammar) => String(grammar.IdIvrGrammar) === String(newGrammarId)
    );

    const { DeReturns } = findGrammar;
    const options = DeReturns.split('|');

    const formattedGrammarOptions = options.map((option) => {
      const [optionName, optionRequired] = option.split('=');

      return {
        _id: Types.ObjectId().toString(),
        description: optionName,
        require_connection: Boolean(optionRequired),
      };
    });

    const updatedVoiceSettings = {
      ...itemSettings,
      idIvrGrammar: newGrammarId,
    };
    const updatedItem = {
      menuItems: formattedGrammarOptions,
      voiceSettings: updatedVoiceSettings,
    };

    setItemSettings(updatedVoiceSettings);
    doUpdateItem(updatedItem);
  };

  return (
    <>
      <p className="handler-title">Áudio + Reconhecimento de Voz</p>

      <SelectGrammar
        value={itemSettings.idIvrGrammar}
        onChange={handleChangeGrammar}
      />

      <div className="common-form-group">
        <label>Texto/áudio - repetição (opcional)</label>
        <InputVoicebotAudioText
          value={itemSettings.deScriptTtsRepeat}
          onChange={handleInputChange}
          name="deScriptTtsRepeat"
          id={`input-item-voicebot-right-menu-${item?._id}`}
        />
      </div>

      <div
        className={classnames('common-form-group', {
          'common-form-group--error': !itemSettings.qtMaxRepeat,
        })}
      >
        <label>Quantidade max. repetição</label>
        <input
          className="form-control mt-1"
          value={itemSettings.qtMaxRepeat || ''}
          name="qtMaxRepeat"
          type="number"
          onChange={handleInputChange}
        />
      </div>

      <div
        className={classnames('common-form-group', {
          'common-form-group--error': !itemSettings.qtScoreMin,
        })}
      >
        <label>Score mínimo aceitável</label>
        <input
          className="form-control mt-1"
          value={itemSettings.qtScoreMin || ''}
          name="qtScoreMin"
          type="number"
          onChange={handleInputChange}
        />
      </div>

      <SelectStatus
        label="Status caso cliente desligue"
        value={itemSettings.idIvrStatus}
        onChange={handleInputChange}
      />

      <div className="form-group row mb-1">
        <div className="custom-control custom-switch mt-2">
          <input
            type="checkbox"
            className="custom-control-input"
            name="mustValidateIvrResultInDatabase"
            id="mustValidateIvrResultInDatabase"
            checked={itemSettings.mustValidateIvrResultInDatabase}
            onChange={handleChangeMustValidateIvrResultInDatabase}
          />
          <label
            className="custom-control-label"
            htmlFor="mustValidateIvrResultInDatabase"
          >
            Validar resposta no banco de dados
          </label>
        </div>
      </div>

      <div className="common-form-group">
        <label>
          Qual campo deve ser utilizado para analisar a resposta (opcional)
        </label>
        <input
          className="form-control mt-1"
          value={itemSettings.nmFieldValidateIvrResultInDatabase}
          name="nmFieldValidateIvrResultInDatabase"
          onChange={handleInputChange}
        />
      </div>

      <div className="common-form-group">
        <label>Nome variável para salvar resposta (opcional)</label>
        <input
          className="form-control mt-1"
          value={itemSettings.nmIvrData}
          name="nmIvrData"
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group row mb-1">
        <div className="custom-control custom-switch mt-2">
          <input
            type="checkbox"
            className="custom-control-input"
            name="mustNextStepWithIvrData"
            id="mustNextStepWithIvrData"
            checked={itemSettings.mustNextStepWithIvrData}
            onChange={handleInputChange}
          />
          <label
            className="custom-control-label"
            htmlFor="mustNextStepWithIvrData"
          >
            Substituir resposta por nextstep
          </label>
        </div>
      </div>

      <div className="common-form-group">
        <label>Código externo do Step (opcional)</label>
        <input
          className="form-control mt-1"
          value={itemSettings.codeIvrStepExternal}
          name="codeIvrStepExternal"
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group row mb-1">
        <div className="custom-control custom-switch mt-2">
          <input
            type="checkbox"
            className="custom-control-input"
            name="isCpc"
            id="isCpc"
            checked={itemSettings.isCpc}
            onChange={handleInputChange}
          />
          <label className="custom-control-label" htmlFor="isCpc">
            É CPC
          </label>
        </div>
      </div>

      <ButtonTestTts text={itemSettings.deScriptTts} />
    </>
  );
};
